// appDashboard — the Dashboard (Dashboard.tsx). Sesja 16 (POLISH 2026-09-11, MAPA 16-pulpit §2):
// "too much text … it has to be simpler everywhere". Five sections, no cards except on the
// first run, no explanatory sentences. Keys mirror ../pl/appDashboard.ts exactly.
//
// The rule that governs this file: EVERY string describes either data we really have, or an
// action the user can really take right now. A section without data gets no sentence about
// being empty — it simply is not there (silence is information). Numbers are typeset by the
// screen (mono, tabular), so keys carry a `{n}` / `{count}` placeholder next to the correctly
// inflected word. Dash = ASCII "-".
export const appDashboard = {
  common: { untitledSeries: 'Untitled series', untitledEdit: 'Untitled edit' },
  error: {
    title: 'Could not load the Dashboard',
    body: 'Your gems, edits and files are all in place - only their status could not be read.',
  },
  // The word pill: in the "now" row and on the stage of a tile without a frame (status ≠ ready only).
  // `statusLive` / `statusFailed` / `statusRendering` are read by components/AutopilotModal.tsx.
  // R5 (rule 30): three state words across the whole product, this old dictionary
  // included — the tile pill in Publishing still reads it. Queue and render are one word.
  thumb: {
    queued: 'queued',
    rendering: 'queued',
    failed: 'error',
    statusLive: 'ready',
    statusFailed: 'error',
    statusRendering: 'queued',
  },
  greeting: {
    hi: 'Hi, {name}',
    hiAnon: 'Hi',
    newBatch: 'New series',
    // ONE status sentence under the greeting, the heaviest state wins: a failed render (24 h) >
    // 0 gems > payments off > a track waiting for lyrics > rendering. Idle = no sentence.
    subRendering_one: 'One edit is rendering in the background.',
    subRendering_few: '{count} edits are rendering in the background.',
    subRendering_many: '{count} edits are rendering in the background.',
    subLyrics_one: 'One track is waiting for lyric corrections.',
    subLyrics_few: '{count} tracks are waiting for lyric corrections.',
    subLyrics_many: '{count} tracks are waiting for lyric corrections.',
    // The refund is said ONLY from the ledger (`refund_confirmed` on every failed render);
    // otherwise the sentence carries no refund.
    subFailed_one: 'One render did not finish - the gems are back.',
    subFailed_few: '{count} renders did not finish - the gems are back.',
    subFailed_many: '{count} renders did not finish - the gems are back.',
    subFailedPending_one: 'One render did not finish.',
    subFailedPending_few: '{count} renders did not finish.',
    subFailedPending_many: '{count} renders did not finish.',
    subZeroGems: '0 gems - rendering cannot start.',
    subBillingOff: 'Payments are temporarily off on our side.',
  },
  // A row of facts instead of KPI tiles (decision 1): three numbers, each a link; a 0 fact disappears.
  facts: {
    label: 'Account',
    gems: '{gems} = {edits}',
    edits_one: '{count} edit',
    edits_few: '{count} edits',
    edits_many: '{count} edits',
    tracks_one: '{count} track',
    tracks_few: '{count} tracks',
    tracks_many: '{count} tracks',
  },
  // "Now" without a heading (decision 2): the series title is the heading; four rows at most.
  // `chunks` is read by components/AutopilotModal.tsx.
  now: {
    readyOf: '{done} of {total}',
    chunks: '{done} of {total} chunks',
    refundBack: 'gems returned (+{gems})',
    retry_one: 'Retry · {cost} gem',
    retry_few: 'Retry · {cost} gems',
    retry_many: 'Retry · {cost} gems',
  },
  recent: {
    allEdits: 'All edits',
    title: 'Recent edits',
    editNo: 'edit {n}',
    // `{period}` = number + word from the screen ("30 days" / "1 day"): the number governs the noun.
    retentionShort: 'Only this account can see these files · available for {period}',
    retry: 'Try again',
  },
  // Autopilot = one row of facts (decision 7); "all active" and "nothing planned" = no fact.
  publishing: {
    heading: 'Autopilot',
    accounts_one: '{n} account',
    accounts_few: '{n} accounts',
    accounts_many: '{n} accounts',
    needsLogin_one: '{n} needs a sign-in',
    needsLogin_few: '{n} need a sign-in',
    needsLogin_many: '{n} need a sign-in',
    scheduled_one: '{n} scheduled',
    scheduled_few: '{n} scheduled',
    scheduled_many: '{n} scheduled',
    next: 'next {when}',
    zeroLine: 'Autopilot publishes on your accounts - none is connected yet.',
    zeroCta: 'Connect an account',
  },
  // "Needs your attention": ONE title with the file name / handle inside + ONE action, no body (decision 4).
  attention: {
    title: 'Needs your attention',
    count_one: '{count}',
    count_few: '{count}',
    count_many: '{count}',
    reauth: '{handle} needs a new sign-in',
    fileFailed: '{name}: could not be analysed',
    analysisPending: '{name}: analysis in progress',
    instrumental: '{name}: no words detected',
    transcriptRetry: '{name}: transcription needs a retry',
    beatsRetry: '{name}: beat analysis needs a retry',
    lowGems: 'Low balance: {gems} = {edits}',
  },
  // `music` = the header's secondary button; `topup` is read by components/AutopilotModal.tsx,
  // `hooks` by routes/OnboardingView.tsx.
  quick: {
    music: 'Upload a track',
    topup: 'Buy gems',
    hooks: 'Hook collection',
  },
  // FIRST RUN = one card (decision 8): one sentence, the button, the formats, steps 2 and 3.
  firstRun: {
    title: 'Start with a track',
    body: 'Upload a file - we pick out the words and beats, then you make your first edits from it.',
    cta: 'Upload a track',
    formats: 'mp3, wav, flac, m4a · up to 200 MB',
    step2Short_one: 'Pick your clips - you already have {count} ready-made pack from us',
    step2Short_few: 'Pick your clips - you already have {count} ready-made packs from us',
    step2Short_many: 'Pick your clips - you already have {count} ready-made packs from us',
    step2ShortNone: 'Pick your clips - upload your own footage, we cut it',
    step3Short: 'Generate your first series - a few edits from one section',
  },
  // Gem signal titles and the actions of "attention" rows; `retryError` = the alert after a failed retry.
  signals: {
    billingTitle: 'Payments are temporarily unavailable on our side',
    zeroTitle: '0 gems - rendering cannot start',
    openBilling: 'Billing',
    openMusic: 'Music',
    writeLyrics: 'Type lyrics manually',
    uploadAnother: 'Upload another version',
    retryTranscript: 'Retry transcription',
    retryAnalysis: 'Retry analysis',
    retryError: 'Could not confirm the retry. Check Edits before trying again - the retry will use the same request.',
  },
}
